import { db } from "../config";
import { collection, doc, setDoc, getDocs, updateDoc, query, where, orderBy, arrayUnion, Timestamp } from "firebase/firestore";
import { isDemoMode } from "../../demo/isDemoMode";
import demoStore from "../../demo/demoStore";

/**
 * Fetch all announcements visible to a vendor (targeted + all-vendor broadcasts).
 * Adds an `isRead` boolean derived from the `readBy` array.
 */
export const getAnnouncementsForVendor = async (userId) => {
  if (isDemoMode) {
    // 1. Get vendor's event IDs from registrations
    const regs = demoStore.getDocs('mpsEventRegistration', [
      { type: 'where', field: 'userId', op: '==', value: userId },
    ]);
    const eventIds = regs.map(r => r.data.eventId).filter(Boolean);
    if (!eventIds.length) return { success: true, announcements: [] };

    // 2. Get all announcements for those events
    const all = demoStore.getDocs('announcements', [
      { type: 'where', field: 'eventId', op: 'in', value: eventIds },
    ]);

    // 3. Filter: broadcast (empty targetVendorIds) or specifically targeted
    const filtered = all
      .filter(a => !a.data.targetVendorIds?.length || a.data.targetVendorIds.includes(userId))
      .map(a => ({
        id: a.id,
        ...a.data,
        isRead: a.data.readBy?.includes(userId) ?? false,
        createdAt: a.data.createdAt instanceof Date ? a.data.createdAt : new Date(a.data.createdAt),
      }))
      .sort((a, b) => b.createdAt - a.createdAt);

    return { success: true, announcements: filtered };
  }

  try {
    // 1. Get vendor's event IDs
    const regSnap = await getDocs(query(
      collection(db, 'mpsEventRegistration'),
      where('userId', '==', userId)
    ));
    const eventIds = regSnap.docs.map(d => d.data().eventId).filter(Boolean);
    if (!eventIds.length) return { success: true, announcements: [] };

    // 2. Firestore 'in' supports max 30 values; chunk if needed
    const chunks = [];
    for (let i = 0; i < eventIds.length; i += 30) chunks.push(eventIds.slice(i, i + 30));

    const all = [];
    for (const chunk of chunks) {
      const snap = await getDocs(query(
        collection(db, 'announcements'),
        where('eventId', 'in', chunk),
        orderBy('createdAt', 'desc')
      ));
      snap.docs.forEach(d => all.push({ id: d.id, ...d.data() }));
    }

    // 3. Filter and annotate with isRead
    const filtered = all
      .filter(a => !a.targetVendorIds?.length || a.targetVendorIds.includes(userId))
      .map(a => ({
        ...a,
        isRead: a.readBy?.includes(userId) ?? false,
        createdAt: a.createdAt?.toDate?.() ?? a.createdAt,
      }))
      .sort((a, b) => b.createdAt - a.createdAt);

    return { success: true, announcements: filtered };
  } catch (error) {
    console.error('Error fetching vendor announcements:', error);
    return { success: false, announcements: [], error: error.message };
  }
};

/**
 * Mark an announcement as read by a vendor (adds userId to readBy array).
 */
export const markAnnouncementAsRead = async (announcementId, userId) => {
  if (isDemoMode) {
    const existing = demoStore.getDoc('announcements', announcementId);
    if (!existing) return { success: false, error: 'Not found' };
    const readBy = existing.readBy || [];
    if (!readBy.includes(userId)) {
      demoStore.updateDoc('announcements', announcementId, { readBy: [...readBy, userId] });
    }
    return { success: true };
  }

  try {
    await updateDoc(doc(db, 'announcements', announcementId), {
      readBy: arrayUnion(userId),
    });
    return { success: true };
  } catch (error) {
    console.error('Error marking announcement as read:', error);
    return { success: false, error: error.message };
  }
};

export const createAnnouncement = async ({ eventId, organizerId, title, message, category, targetVendorIds = [] }) => {
  try {
    if (!eventId || !organizerId || !title || !message) {
      return { success: false, error: "Missing required fields" };
    }

    const announcementsRef = collection(db, "announcements");
    const newAnnouncementRef = doc(announcementsRef);

    const announcementData = {
      id: newAnnouncementRef.id,
      eventId,
      organizerId,
      title,
      message,
      category: category || "general",
      targetVendorIds,
      readBy: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    await setDoc(newAnnouncementRef, announcementData);

    return { success: true, announcementId: newAnnouncementRef.id };
  } catch (error) {
    console.error("Error creating announcement:", error);
    return { success: false, error: error.message };
  }
};

export const getAnnouncementsForEvent = async (eventId) => {
  try {
    if (!eventId) {
      return { success: false, announcements: [], error: "Event ID is required" };
    }

    const announcementsRef = collection(db, "announcements");
    const q = query(
      announcementsRef,
      where("eventId", "==", eventId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const announcements = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      announcements.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
      });
    });

    return { success: true, announcements };
  } catch (error) {
    console.error("Error fetching announcements:", error);
    return { success: false, announcements: [], error: error.message };
  }
};
